import { Badge } from '@/components/ui/badge'
import { DriverAvatar } from '@/components/ui/driver-avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatDateOnly } from '@/core/shared/utils'
import {
  Phone,
  Mail,
  MapPin,
  Calendar,
  Car,
  MessageCircle,
  BarChart,
  ShieldCheck,
} from 'lucide-react'

interface DriverInfoProps {
  driverData: {
    id: string
    name: string
    avatar?: string
    status: string
    rating: string
    classification: string
    lastRideAt: string
    phone: string
    email: string
    address: string
    joinDate: string
  }
  vehicleInfo: {
    model: string
    plate: string
    color: string
  } | null
  documents: {
    cnh: {
      expires?: string
    }
    ipva: {
      expires?: string
    }
    insurance: {
      expires?: string
    }
  } | null
  onCallClick: () => void
  onWhatsAppClick: () => void
}

export default function DriverInfo({
  driverData,
  vehicleInfo,
  documents,
  onCallClick,
  onWhatsAppClick,
}: DriverInfoProps) {
  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <DriverAvatar
                  photo={driverData.avatar}
                  name={driverData.name}
                  size="lg"
                />
                <h2 className="text-lg sm:text-xl font-bold">
                  {driverData.name}
                </h2>
              </div>
              <Badge
                variant={
                  driverData.status === 'online' ? 'default' : 'secondary'
                }
                className="text-xs px-3"
              >
                {driverData.status === 'online' ? 'Online' : 'Offline'}
              </Badge>
            </div>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Phone className="h-4 w-4" />
                <span>{driverData.phone}</span>
              </div>
              <div className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                <span>{driverData.email}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span>{driverData.address}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 " />
                <span>
                  Desde{' '}
                  {new Date(driverData.joinDate).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {driverData.phone ? (
              <Button
                asChild
                variant="default"
                size="sm"
                className="w-full bg-green-600 hover:bg-green-700 text-white border border-[hsl(var(--brand-color))] min-h-12"
                onClick={onWhatsAppClick}
              >
                <a
                  href={`https://wa.me/55${driverData.phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Chamar no WhatsApp
                </a>
              </Button>
            ) : (
              <Button disabled variant="secondary" size="sm" className="w-full">
                <MessageCircle className="h-4 w-4 mr-2" />
                Número não disponível
              </Button>
            )}
            <Button
              variant="outline"
              className="flex items-center gap-2 bg-transparent border-[hsl(var(--brand-color))] text-[hsl(var(--brand-color))] hover:bg-[hsl(var(--brand-color)/0.1)] min-h-12"
              onClick={onCallClick}
            >
              <Phone className="h-5 w-5" />
              Ligar
            </Button>
          </div>
        </div>

        <Separator className="my-4" />

        <div className="flex flex-col gap-6">
          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2 text-[hsl(var(--brand-color))] text-base">
              <BarChart className="h-5 w-5" />
              Atividade & Avaliação
            </h3>
            <div className="space-y-2 text-sm">
              {driverData.classification && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Classificação:</span>
                  <span className="font-medium">
                    {driverData.classification}
                  </span>
                </div>
              )}
              {driverData.lastRideAt &&
                driverData.lastRideAt !== 'Nenhuma Corrida Realizada' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Última corrida:
                    </span>
                    <span className="font-medium">{driverData.lastRideAt}</span>
                  </div>
                )}
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2 text-[hsl(var(--brand-color))] text-base">
              <Car className="h-5 w-5" />
              Informações do Veículo
            </h3>
            {vehicleInfo && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Modelo:</span>
                  <span className="font-medium">{vehicleInfo.model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Placa:</span>
                  <span className="font-medium">{vehicleInfo.plate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cor:</span>
                  <span className="font-medium">{vehicleInfo.color}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2 text-[hsl(var(--brand-color))] text-base">
              <ShieldCheck className="h-5 w-5" />
              Status dos Documentos
            </h3>
            {documents && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">CNH:</span>
                  <div className="flex items-center gap-2">
                    {documents.cnh.expires && documents.cnh.expires !== 'N/A' && (
                      <span className="text-sm font-medium">
                        válida até {formatDateOnly(documents.cnh.expires)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    IPVA do Veículo:
                  </span>
                  {documents.ipva?.expires && documents.ipva.expires !== 'N/A' && (
                    <span className="text-sm font-medium">
                      {documents.ipva.expires}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Seguro:</span>
                  {documents.insurance?.expires && documents.insurance.expires !== 'N/A' && (
                    <span className="text-sm font-medium">
                      {documents.insurance.expires}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
